/**
 * L'énoncé qui défile, et qui le dit (lives des 23 et 24/10 : au web, sa barre était masquée, et la coupe pouvait
 * tomber entre deux blocs sans rien laisser voir de la suite) :
 *  - au web, la barre fine du design system (`<ScrollView>` du DS), montrée au web seulement ;
 *  - partout, un dégradé vers le fond au bas de l'énoncé tant qu'il reste à lire, qui s'en va à la fin
 *    (`moreBelow`) ; il ne prend aucun geste, et s'arrête avant la barre ;
 *  - au grand écran du live, la molette au-dessus de la visio le fait défiler (`useWheelRelayTarget`).
 */
import { LinearGradient } from "expo-linear-gradient"
import React, { useRef, useState } from "react"
import { Animated, Platform, StyleSheet as RNStyleSheet, View, type StyleProp, type ViewStyle } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { usePresence } from "../../hooks/usePresence"
import { useWheelRelayTarget } from "../../hooks/useWheelRelay"
import { hexWithAlpha } from "../../utils/shadow"
import { DS_SCROLLBAR_WIDTH } from "../../utils/webScrollbars"

/** Deux pixels de jeu : au web, les mesures arrondies d'un défilement arrivé en bas ne tombent pas toujours juste. */
const SLACK_PX = 2

/** Il reste de l'énoncé sous le bas de l'écran. */
export function moreBelow(box: { viewport: number; content: number; offset: number }): boolean {
    if (box.viewport <= 0) return false
    return box.content - box.viewport - box.offset > SLACK_PX
}

/**
 * La couleur, sans opacité : vers un noir transparent, le dégradé passerait par le gris au natif. Une variable CSS (le
 * web) passe par `transparent`, que le navigateur mêle sans gris ; `color-mix` n'y serait pas lu par le dégradé.
 */
export function clearOf(color: string): string {
    return hexWithAlpha(color, 0) ?? "transparent"
}

/** Le dégradé : la hauteur d'une ligne et demie de l'énoncé, qu'il commence à voiler. */
const FADE_HEIGHT = 48

export function StatementScroll(props: { contentContainerStyle: StyleProp<ViewStyle>; children: React.ReactNode }) {
    const { contentContainerStyle, children } = props
    const { theme } = useUnistyles()
    const scroller = useRef<ScrollView>(null)
    useWheelRelayTarget(scroller)
    // État d'interface : il reste de l'énoncé sous le bas de l'écran. Les mesures vivent dans une ref : le
    // défilement les change à chaque image, seul le passage du seuil redessine.
    const box = useRef({ viewport: 0, content: 0, offset: 0 })
    const [more, setMore] = useState(false)
    const measure = (next: Partial<typeof box.current>) => {
        box.current = { ...box.current, ...next }
        setMore(moreBelow(box.current))
    }
    const fade = usePresence(more)
    const surface = theme.colors.surface

    return (
        <View style={styles.fill}>
            <ScrollView
                ref={scroller}
                showsVerticalScrollIndicator={Platform.OS === "web"}
                contentContainerStyle={contentContainerStyle}
                scrollEventThrottle={32}
                onLayout={(event) => measure({ viewport: event.nativeEvent.layout.height })}
                onContentSizeChange={(_width, height) => measure({ content: height })}
                // Le défilement porte aussi les deux tailles : un contenu qui a rétréci sans le redire (le web,
                // l'énoncé passé du plein écran au volet à côté de la visio) ne garde pas le dégradé au bout.
                onScroll={({ nativeEvent }) =>
                    measure({
                        offset: nativeEvent.contentOffset.y,
                        content: nativeEvent.contentSize.height,
                        viewport: nativeEvent.layoutMeasurement.height,
                    })
                }
            >
                {children}
            </ScrollView>
            {fade.mounted && (
                <Animated.View
                    style={[styles.fade, { opacity: fade.progress }]}
                    pointerEvents="none"
                    testID="statement-more-below"
                >
                    <LinearGradient colors={[clearOf(surface), surface]} style={RNStyleSheet.absoluteFill} />
                </Animated.View>
            )}
        </View>
    )
}

const styles = StyleSheet.create(() => ({
    fill: { flex: 1, minHeight: 0 },
    /** Au bas de l'énoncé, à côté de sa barre au web : le dégradé ne la voile pas. */
    fade: {
        position: "absolute",
        left: 0,
        right: Platform.OS === "web" ? DS_SCROLLBAR_WIDTH : 0,
        bottom: 0,
        height: FADE_HEIGHT,
    },
}))
